import React, { useState, useCallback, useEffect } from "react";
import {
    Stack,
    Typography,
    Button,
    List,
    ListItem,
    ListItemButton,
    Box,
    CircularProgress,
    Textarea,
    Card,
    Chip,
    Divider,
    Theme,
} from "@mui/joy";
import {
    CheckCircle as CheckCircleIcon,
    NavigateNext as NavigateNextIcon,
    Done as DoneIcon,
    ExpandMore,
    ExpandLess,
    Sort,
    ContentCopy,
} from "@mui/icons-material";
import { Link as ReactRouterLink } from "react-router-dom";
import { setPageTitle } from "../../util/elementUtil";
import { FileApi } from "../../api/file";
import { WorkflowApi } from "../../api/workflow";
import { copyToClipboardOrShowAlert } from "../../util/objectUtil";
import { UploadRounded, CheckCircle, NoteAdd } from "@mui/icons-material";
import { FileUpload, FileUploadProps } from "../FileUpload";
import { FileWithPath } from "react-dropzone";
import { useQuery } from "react-query";
import { PrimeQueryKeys } from "../../shared/constants";
import FileIcon from "../FileIcon";
import { ApiFile, ApiFragmentNode, ApiReference } from "../../shared/interfaces";
import { FileUploader } from "../../util/FileUploader";
import { MarkdownRenderer } from "../MarkdownRenderer";
import FileViewer, { FileViewerArgs } from "../fileviewer/FileViewer";
import { convertReferencesToMarkdown, ReferenceProcessor } from "../../util/references";
import { Panel, PanelGroup } from "react-resizable-panels";
import { StyledPanelResizeHandle } from "../StyledPanelResizeHandle";
import FloatingRelatedItems from "../nodes/FloatingRelatedItems";

// Memoize the FileViewer component to prevent unnecessary re-renders
const FileViewerMemo = React.memo(FileViewer);

const styles = {
    topContainer: {
        flex: "1 1 auto",
        position: "relative",
        overflow: "hidden", // Change from overflowY: "auto" to overflow: "hidden"
        display: "flex",
        flexDirection: "column",
        height: "100%", // Add explicit height
    },
    pageTitleContainer: (theme: Theme) => ({
        px: 3,
        py: 2,
        boxShadow: theme.vars.shadow.md,
        zIndex: 500,
    }),
    pageTitle: {
        fontFamily: "Overpass",
        display: "run-in",
    },
    uploadInput: {
        display: "none",
    },
    dropZone: {
        border: "2px dashed var(--joy-palette-neutral-outlinedBorder)",
        borderRadius: "8px",
        p: 1,
        mt: 1,
        mb: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "90px",
        backgroundColor: "var(--joy-palette-background-surface)",
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
    },
    dropZoneActive: {
        borderColor: "var(--joy-palette-primary-500)",
        backgroundColor: "var(--joy-palette-primary-100)",
    },
    buttonContainer: {
        width: "100%",
        maxWidth: "600px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
    },
    actionButton: {
        width: "100%",
    },
    filesSection: {
        gap: 2,
        height: "100%",
        overflow: "auto",
    },
    selectedItem: {
        backgroundColor: "var(--joy-palette-primary-softBg)",
        "&:hover": {
            backgroundColor: "var(--joy-palette-primary-softHoverBg)",
        },
    },
    checkIcon: {
        color: "var(--joy-palette-success-400)",
        ml: 1,
    },
    fileItem: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
    },
    fileName: {
        display: "flex",
        alignItems: "center",
        gap: 1,
        flex: 1,
        minWidth: 0,
        wordBreak: "break-word",
        overflowWrap: "break-word",
    },
    textarea: {
        mb: 0,
        width: "100%",
        height: "86px",
        p: "10px 12px",
    },
    errorText: {
        mb: 0,
    },
    stepCard: {
        mb: 1,
        p: "10px 12px",
    },
    stepHeader: {
        mb: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 2,
    },
    stepContent: {
        mt: 0,
    },
    stepChip: {
        width: 32,
        height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    workflowContainer: {
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    stepsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 2,
        mt: 0,
    },
    verticalDivider: {
        mx: 2,
        borderColor: "var(--joy-palette-neutral-outlinedBorder)",
    },
    mainContainer: {
        display: "flex",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        p: 3,
    },
    fileColumnContainer: {
        width: "25%",
        mr: 0,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    mainContentContainer: {
        flex: 1,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    hiddenFilesText: {
        color: "text.tertiary",
        textAlign: "center",
        py: 2,
    },
    fileListContainer: {
        width: "100%",
        px: 0,
    },
    spacingMt1: {
        mt: 1,
    },
    spacingMt2: {
        mt: 2,
    },
    spacingMb1: {
        mb: 1,
    },
    spacingMb2: {
        mb: 2,
    },
    spacingMl1: {
        ml: 1,
    },
    spacingPy2: {
        py: 2,
    },
    spacingPx0: {
        px: 0,
    },
    spacingP4: {
        p: 4,
    },
    spacingTextAlignLeft: {
        textAlign: "left",
    },
    spacingTextAlignCenter: {
        textAlign: "center",
    },
    spacingFontSizeSm: {
        fontSize: "sm",
    },
    spacingMt05: {
        mt: 0.5,
    },
    spacingP10x12: {
        p: "10px 12px",
    },
    spacingProcessingCard: {
        p: "16px",
        height: "auto",
        minHeight: "86px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow: "none",
        border: "1px solid var(--joy-palette-neutral-200)",
    },
    fileViewerContainer: {
        height: "calc(100vh - 220px)",
    },
    fileViewerLeftColumn: {
        width: "40%",
        overflowY: "auto",
    },
    fileViewerRightColumn: {
        width: "60%",
    },
    welcomeCard: {
        p: 4,
        textAlign: "center",
    },
    welcomeTextContainer: {
        textAlign: "left",
        ml: 2,
    },
    uploadIcon: {
        mb: 1,
        fontSize: "2rem",
    },
    supportedFormatsText: {
        opacity: 0.7,
        mt: 0.5,
        textAlign: "center",
    },
    fullWidthRow: {
        width: "100%",
    },
    copyButtonStyle: {
        mt: 1,
        minWidth: 140,
        "&:hover": {
            bgcolor: "neutral.softHoverBg",
        },
    },
    uploadingFileBox: {
        mt: 1,
        mb: 1,
    },
    uploadStatusText: {
        mb: 0.5,
    },
    simpleDivider: {
        my: 1,
    },
    fileListTools: {
        mb: 1,
    },
    spacingMy1: {
        my: 1,
    },
    spacingMb05: {
        mb: 0.5,
    },
    spacingMr0: {
        mr: 0,
    },
    spacingPx3: {
        px: 3,
    },
    spacingPy4: {
        py: 4,
    },
    spacingP3: {
        p: 3,
    },
    spacingP2: {
        p: 2,
    },
};

const cleanReviewResponse = (response: string): string => {
    return response.replace(/^(RFI_NEEDED|NO_RFI_NEEDED)[:\s]*/i, "").trim();
};

const createReferenceProcessor = (
    necessityCheck: {
        reviewResponse: string;
        references?: Record<string, ApiReference>;
    },
    existingFiles: ApiFile[],
) => {
    if (!necessityCheck?.references || Object.keys(necessityCheck.references).length === 0) {
        console.log("No references available to process");
        return undefined;
    }

    if (!existingFiles || existingFiles.length === 0) {
        console.log("No files available for reference processing");
        return undefined;
    }

    const mockMessage = {
        references: necessityCheck.references,
        files: existingFiles,
        message: necessityCheck.reviewResponse,
        id: "rfi-review",
        createdAt: new Date().toISOString(),
        messageType: "message",
        user: { id: "system", name: "System" },
        conversationId: "rfi-workflow",
    } as any;

    return new ReferenceProcessor(mockMessage);
};

export default function RfiReviewWorkflow() {
    // We'll move the initialization effect after defining handleStartNewReview

    const [uploadedFile, setUploadedFile] = useState<FileUploadProps | null>(null);
    const [selectedFile, setSelectedFile] = useState<ApiFile | null>(null);
    const [isLoading] = useState(false); // For file upload/refresh only
    const [isConverting, setIsConverting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isRefreshingFiles, setIsRefreshingFiles] = useState(false);
    // Step-specific loading states
    const [isExtractingQuestion, setIsExtractingQuestion] = useState(false);
    const [isCheckingNecessity, setIsCheckingNecessity] = useState(false);
    const [isCreatingRfi, setIsCreatingRfi] = useState(false);

    // State for multi-step RFI process
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [extractedQuestion, setExtractedQuestion] = useState<string | null>(null);
    const [necessityCheck, setNecessityCheck] = useState<{
        needsRfi: boolean;
        reviewResponse: string;
        relevantSubsections: string[];
        references?: Record<string, ApiReference>;
    } | null>(null);
    const [rfiDraft, setRfiDraft] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [rfiText, setRfiText] = useState<string>("");
    const [filesExpanded, setFilesExpanded] = useState<boolean>(true);
    const [sortByRecent, setSortByRecent] = useState<boolean>(true);
    const [copied, setCopied] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // File viewer state
    const [currentFileViewerFile, setCurrentFileViewerFile] = useState<FileViewerArgs | null>(null);
    const [selectedFragmentNode, setSelectedFragmentNode] = useState<ApiFragmentNode | null>(null);

    // Add an effect to handle cleanup of file viewer when component is unmounted
    useEffect(() => {
        return () => {
            // Clean up any resources when component unmounts
            setCurrentFileViewerFile(null);
            setSelectedFragmentNode(null);
        };
    }, []);

    // Review started state
    const [reviewStarted, setReviewStarted] = useState(false);

    // Fetch all existing files
    const { data: fileData, refetch: refetchFiles } = useQuery(
        PrimeQueryKeys.allFiles,
        FileApi.getAllFiles,
    );

    // Automatically extract question and check necessity when a file is selected
    useEffect(() => {
        const autoExtractAndCheck = async () => {
            if (selectedFile) {
                setIsExtractingQuestion(true);
                setError(null);
                try {
                    const response = await WorkflowApi.extractRfiQuestion(selectedFile.id);
                    setExtractedQuestion(response.question);
                    setCurrentStep(1);
                    setIsExtractingQuestion(false);
                    setIsCheckingNecessity(true);
                    try {
                        const necessityResponse = await WorkflowApi.checkRfiNecessity(
                            selectedFile.id,
                        );
                        necessityResponse.reviewResponse = cleanReviewResponse(
                            necessityResponse.reviewResponse,
                        );
                        setCurrentFileViewerFile(null);
                        const responseWithRefs = necessityResponse as {
                            needsRfi: boolean;
                            reviewResponse: string;
                            relevantSubsections: string[];
                            references?: Record<string, ApiReference>;
                        };
                        setNecessityCheck(responseWithRefs);
                        setCurrentStep(2);
                    } catch (err) {
                        setError(
                            err instanceof Error ? err.message : "An unexpected error occurred",
                        );
                    } finally {
                        setIsCheckingNecessity(false);
                    }
                } catch (error) {
                    setError(
                        error instanceof Error ? error.message : "An unexpected error occurred",
                    );
                    setIsExtractingQuestion(false);
                }
            }
        };
        // Only auto-run if a file is selected and not already in step 2
        if (selectedFile && currentStep === 0) {
            autoExtractAndCheck();
        }
    }, [selectedFile, currentStep]);

    // Sort files by date or alphabetically based on sortByRecent
    const existingFiles = React.useMemo(() => {
        const files = [...(fileData ?? [])];
        if (sortByRecent) {
            return files.sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            );
        } else {
            return files.sort((a, b) => a.fileName.localeCompare(b.fileName));
        }
    }, [fileData, sortByRecent]);

    // Helper to process file upload (used by both file select and drop)
    const processFileUpload = useCallback(
        async (file: FileWithPath) => {
            const newFile = FileUploader.createFileUploadProps(file, 0);
            setUploadedFile(newFile);
            try {
                const uploadedFileResult = await FileApi.uploadFile(file, (fileId, progress) => {
                    setUploadedFile((prev) =>
                        prev && prev.id === newFile.id ? { ...prev, progress } : prev,
                    );
                });
                setSelectedFile(uploadedFileResult);
                setIsRefreshingFiles(true);
                await refetchFiles();
                setIsRefreshingFiles(false);
                setTimeout(() => {
                    setUploadedFile(null);
                }, 1000);
            } catch (error) {
                console.error("Error uploading file:", error);
                setUploadedFile((prev) =>
                    prev && prev.id === newFile.id ? { ...prev, progress: -1 } : prev,
                );
            }
        },
        [refetchFiles],
    );

    // Combined drag enter/over handler
    const handleDragEnterOrOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (rfiText.trim()) return;
            setIsDragging(true);
        },
        [rfiText],
    );

    // Workflow state reset helper
    const resetWorkflowState = useCallback(() => {
        setSelectedFile(null);
        setCurrentStep(0);
        setExtractedQuestion(null);
        setNecessityCheck(null);
        setRfiDraft(null);
        setError(null);
        setRfiText("");
        setUploadedFile(null);
        setIsDragging(false);
        setCurrentFileViewerFile(null);
        setSelectedFragmentNode(null);
        setReviewStarted(false);
    }, []);

    // Reset workflow state and start a new review
    const handleStartNewReview = useCallback(() => {
        resetWorkflowState();
        setReviewStarted(true);
    }, [resetWorkflowState]);

    // Update message handler to include integration with chat if needed later
    const handleMessageFromRelatedItems = useCallback((message: string, node: ApiFragmentNode) => {
        // For now, just log the message to the console
        // In the future, this could be extended to send the message to a conversation or workflow step
        console.log("Message from related items:", message, "for node:", node);

        // You could implement additional functionality here, such as:
        // 1. Create a new conversation with this message
        // 2. Add this information to the RFI
        // 3. Store this as a note or comment
    }, []);

    // Run only once when the component mounts
    useEffect(() => {
        setPageTitle("RFI Review");
        handleStartNewReview(); // Automatically start new review on mount
    }, [handleStartNewReview]);

    // Handle copy to clipboard with visual feedback
    const handleCopyToClipboard = () => {
        copyToClipboardOrShowAlert(rfiDraft || "");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    };

    const handleTextSubmit = useCallback(async () => {
        if (!rfiText.trim()) {
            setError("Please enter some text first");
            return;
        }

        setIsConverting(true);
        setError(null);

        try {
            // Call the direct text-based workflow using checkRfiNecessityText
            // This skips the file creation and extraction steps
            const necessityCheckResult = await WorkflowApi.checkRfiNecessityText(rfiText);

            // Close any existing file viewer when moving to a new step
            setCurrentFileViewerFile(null);

            setExtractedQuestion(rfiText);

            // Cast response to include potential references field
            const responseWithRefs = necessityCheckResult as {
                needsRfi: boolean;
                reviewResponse: string;
                relevantSubsections: string[];
                references?: Record<string, ApiReference>;
            };

            setNecessityCheck(responseWithRefs);
            setCurrentStep(2);
            setRfiText("");
            setError(null);
            setReviewStarted(false); // Enable Start New Review button after text-based flow
        } catch (error) {
            console.error("Error in handleTextSubmit:", error);
            setError(error instanceof Error ? error.message : "Failed to process RFI text");
        } finally {
            setIsConverting(false);
        }
    }, [rfiText]);

    const handleFileSelect = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            if (!files || files.length === 0) return;
            const file = files[0] as unknown as FileWithPath;
            await processFileUpload(file);
            if (fileInputRef.current) fileInputRef.current.value = "";
        },
        [processFileUpload],
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback(
        async (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            if (rfiText.trim()) return;
            const files = e.dataTransfer.files;
            if (!files || files.length === 0) return;
            const file = files[0] as unknown as FileWithPath;
            await processFileUpload(file);
        },
        [rfiText, processFileUpload],
    );

    const handleCreateRfi = async () => {
        if (!necessityCheck) return;

        setIsCreatingRfi(true);
        setError(null);

        try {
            const cleanedResponse = cleanReviewResponse(necessityCheck.reviewResponse);
            let response: {
                rfiDraft: string;
                references?: Record<string, ApiReference>;
            };

            if (selectedFile) {
                // File-based RFI creation
                response = await WorkflowApi.createRfi(
                    selectedFile.id,
                    cleanedResponse,
                    necessityCheck.relevantSubsections,
                );
            } else {
                // Text-based RFI creation
                response = await WorkflowApi.createRfiText(
                    extractedQuestion || "",
                    cleanedResponse,
                    necessityCheck.relevantSubsections,
                );
            }

            setRfiDraft(response.rfiDraft);
            setNecessityCheck((prevState) => ({
                ...prevState!,
                references: response.references || prevState?.references,
            }));
            setCurrentStep(2);
        } catch (error) {
            console.error("Error creating RFI:", error);
            setError(error instanceof Error ? error.message : "An unexpected error occurred");
        } finally {
            setIsCreatingRfi(false);
        }
    };

    // Render content based on the current step
    const renderStepContent = () => {
        return (
            <Stack sx={styles.stepsContainer}>
                {/* Step 1: Extract Question - only for file-based processing */}
                {selectedFile && !isLoading && !isRefreshingFiles && (
                    <Card
                        variant={currentStep === 0 ? "soft" : "outlined"}
                        sx={{
                            ...styles.stepCard,
                            borderColor: currentStep === 0 ? "primary.softBorder" : undefined,
                        }}
                    >
                        <Box sx={styles.stepHeader}>
                            <Box
                                sx={{
                                    ...styles.stepChip,
                                    bgcolor:
                                        currentStep > 0
                                            ? "success.softBg"
                                            : currentStep === 0
                                              ? "primary.softBg"
                                              : "neutral.softBg",
                                }}
                            >
                                {currentStep > 0 ? (
                                    <DoneIcon fontSize="small" color="success" />
                                ) : (
                                    <Typography level="body-sm">1</Typography>
                                )}
                            </Box>
                            <Typography level="title-md">Extract RFI Question</Typography>
                        </Box>
                        <Divider />
                        <Box sx={styles.stepContent}>
                            {isExtractingQuestion ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size="sm" />
                                    <Typography>Extracting question...</Typography>
                                </Stack>
                            ) : extractedQuestion ? (
                                <Box sx={styles.stepContent}>
                                    <Typography level="body-sm" fontWeight="md">
                                        Extracted Question:
                                    </Typography>
                                    <Typography>{extractedQuestion}</Typography>
                                </Box>
                            ) : null}
                        </Box>
                    </Card>
                )}

                {/* Step 2: Check Necessity */}
                {currentStep >= 1 && (
                    <Card
                        variant={currentStep === 1 ? "soft" : "outlined"}
                        sx={{
                            ...styles.stepCard,
                            borderColor: currentStep === 1 ? "primary.softBorder" : undefined,
                        }}
                    >
                        <Box sx={styles.stepHeader}>
                            <Box
                                sx={{
                                    ...styles.stepChip,
                                    bgcolor:
                                        currentStep > 1
                                            ? "success.softBg"
                                            : currentStep === 1
                                              ? "primary.softBg"
                                              : "neutral.softBg",
                                }}
                            >
                                {currentStep > 1 ? (
                                    <DoneIcon fontSize="small" color="success" />
                                ) : (
                                    <Typography level="body-sm">2</Typography>
                                )}
                            </Box>
                            <Typography level="title-md">Check RFI Necessity</Typography>
                        </Box>
                        <Divider />
                        <Box sx={styles.stepContent}>
                            {isCheckingNecessity ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size="sm" />
                                    <Typography>Checking necessity...</Typography>
                                </Stack>
                            ) : necessityCheck ? (
                                <Box sx={styles.stepContent}>
                                    <MarkdownRenderer
                                        message={convertReferencesToMarkdown(
                                            cleanReviewResponse(necessityCheck.reviewResponse),
                                        )}
                                        contentId="rfi-necessity-check"
                                        processLinks={!!necessityCheck?.references}
                                        currentFileViewerFile={currentFileViewerFile}
                                        setCurrentFileViewerFile={setCurrentFileViewerFile}
                                        referenceProcessor={
                                            necessityCheck?.references
                                                ? createReferenceProcessor(
                                                      necessityCheck,
                                                      existingFiles,
                                                  )
                                                : undefined
                                        }
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    </Card>
                )}

                {/* Step 3: Generate & Review RFI Draft - always available after necessity check */}
                {currentStep >= 2 && (
                    <Card
                        variant={currentStep >= 2 ? "soft" : "outlined"}
                        sx={{
                            ...styles.stepCard,
                            borderColor: currentStep >= 2 ? "primary.softBorder" : undefined,
                        }}
                    >
                        <Box sx={styles.stepHeader}>
                            <Box
                                sx={{
                                    ...styles.stepChip,
                                    bgcolor: rfiDraft ? "success.softBg" : "primary.softBg",
                                }}
                            >
                                {rfiDraft ? (
                                    <DoneIcon fontSize="small" color="success" />
                                ) : (
                                    <Typography level="body-sm">3</Typography>
                                )}
                            </Box>
                            <Typography level="title-md">
                                {rfiDraft ? "Review RFI Draft" : "Generate RFI Draft"}
                            </Typography>
                        </Box>
                        <Divider />
                        <Box sx={styles.stepContent}>
                            {currentStep === 2 && !rfiDraft ? (
                                <Button
                                    onClick={handleCreateRfi}
                                    loading={isCreatingRfi}
                                    disabled={isCreatingRfi}
                                    sx={styles.spacingMt1}
                                    endDecorator={<NavigateNextIcon />}
                                >
                                    Generate RFI Draft
                                </Button>
                            ) : null}
                            {rfiDraft && (
                                <>
                                    <Box sx={styles.spacingMt1}>
                                        <MarkdownRenderer
                                            message={convertReferencesToMarkdown(rfiDraft)}
                                            contentId="rfi-draft"
                                            processLinks={!!necessityCheck?.references}
                                            currentFileViewerFile={currentFileViewerFile}
                                            setCurrentFileViewerFile={setCurrentFileViewerFile}
                                            referenceProcessor={
                                                necessityCheck?.references
                                                    ? createReferenceProcessor(
                                                          necessityCheck,
                                                          existingFiles,
                                                      )
                                                    : undefined
                                            }
                                        />
                                    </Box>
                                    <Button
                                        variant="plain"
                                        color={copied ? "success" : "neutral"}
                                        size="sm"
                                        startDecorator={
                                            copied ? <CheckCircleIcon /> : <ContentCopy />
                                        }
                                        sx={styles.copyButtonStyle}
                                        onClick={handleCopyToClipboard}
                                    >
                                        {copied ? "Copied!" : "Copy to Clipboard"}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Card>
                )}
            </Stack>
        );
    };

    return (
        <Stack sx={styles.topContainer}>
            {selectedFragmentNode && (
                <FloatingRelatedItems
                    fragmentNode={selectedFragmentNode}
                    onClose={() => setSelectedFragmentNode(null)}
                    setFragmentNode={(node) => setSelectedFragmentNode(node)}
                    onMessageSent={handleMessageFromRelatedItems}
                />
            )}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={styles.pageTitleContainer}
            >
                <Stack direction="row" spacing={2}>
                    <Typography level="title-lg" sx={styles.pageTitle}>
                        RFI Review
                    </Typography>
                </Stack>
            </Stack>

            <input
                type="file"
                ref={fileInputRef}
                style={styles.uploadInput}
                onChange={handleFileSelect}
            />

            <Box
                sx={styles.mainContainer}
                onDragEnter={reviewStarted ? handleDragEnterOrOver : undefined}
                onDragOver={reviewStarted ? handleDragEnterOrOver : undefined}
                onDragLeave={reviewStarted ? handleDragLeave : undefined}
                onDrop={reviewStarted ? handleDrop : undefined}
            >
                <Box sx={styles.fileColumnContainer}>
                    <Stack sx={styles.filesSection}>
                        <Box sx={styles.buttonContainer}>
                            <Textarea
                                placeholder="Enter your RFI here for direct processing..."
                                minRows={3}
                                maxRows={6}
                                value={rfiText}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                    setRfiText(e.target.value)
                                }
                                sx={styles.textarea}
                                color={error ? "danger" : "neutral"}
                                disabled={!reviewStarted}
                            />
                            {error && (
                                <Typography level="body-sm" color="danger" sx={styles.errorText}>
                                    {error}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2} sx={styles.fullWidthRow}>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={handleTextSubmit}
                                    disabled={isConverting || !rfiText.trim() || !reviewStarted}
                                    sx={styles.actionButton}
                                    startDecorator={
                                        isConverting ? <CircularProgress size="sm" /> : <NoteAdd />
                                    }
                                >
                                    {isConverting ? "Processing..." : "Process RFI text"}
                                </Button>
                            </Stack>

                            {/* Drag & Drop Zone - always shown but styled differently when text input has content */}
                            <Box
                                sx={{
                                    ...styles.dropZone,
                                    ...(isDragging && !rfiText.trim() && reviewStarted
                                        ? styles.dropZoneActive
                                        : {}),
                                    ...(rfiText.trim()
                                        ? {
                                              opacity: 0.5,
                                              pointerEvents: "none",
                                              borderColor: "var(--joy-palette-neutral-300)",
                                          }
                                        : {}),
                                    ...(!reviewStarted
                                        ? {
                                              opacity: 0.5,
                                              pointerEvents: "none",
                                              borderColor: "var(--joy-palette-neutral-300)",
                                          }
                                        : {}),
                                }}
                                onDragEnter={reviewStarted ? handleDragEnterOrOver : undefined}
                                onDragOver={reviewStarted ? handleDragEnterOrOver : undefined}
                                onDragLeave={reviewStarted ? handleDragLeave : undefined}
                                onDrop={reviewStarted ? handleDrop : undefined}
                                onClick={() =>
                                    reviewStarted &&
                                    !rfiText.trim() &&
                                    fileInputRef.current?.click()
                                }
                            >
                                <UploadRounded
                                    sx={{
                                        ...styles.uploadIcon,
                                        color:
                                            rfiText.trim() || !reviewStarted
                                                ? "var(--joy-palette-neutral-500)"
                                                : "var(--joy-palette-primary-500)",
                                    }}
                                />
                                <Typography
                                    level="body-md"
                                    textAlign="center"
                                    color={rfiText.trim() || !reviewStarted ? "neutral" : "primary"}
                                >
                                    {!reviewStarted
                                        ? 'Click "Start New Review" to begin'
                                        : rfiText.trim()
                                          ? "Clear text input to enable file upload"
                                          : isDragging
                                            ? "Upload"
                                            : "Drag & drop a file here, or click to select"}
                                </Typography>
                                <Typography
                                    level="body-sm"
                                    sx={styles.supportedFormatsText}
                                    color="neutral"
                                >
                                    Supported formats: PDF, PNG, JPG, etc.
                                </Typography>
                            </Box>

                            <Stack direction="row" spacing={2} sx={styles.fullWidthRow}>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={handleStartNewReview}
                                    disabled={reviewStarted}
                                    sx={styles.actionButton}
                                >
                                    Start New Review
                                </Button>
                            </Stack>
                        </Box>

                        {uploadedFile && (
                            <Box sx={styles.uploadingFileBox}>
                                <Typography level="body-sm" sx={styles.uploadStatusText}>
                                    {uploadedFile.progress < 100 && uploadedFile.progress !== -1
                                        ? "Uploading file..."
                                        : uploadedFile.progress === -1
                                          ? "Upload failed:"
                                          : "Upload complete:"}
                                </Typography>
                                <FileUpload file={uploadedFile} />
                            </Box>
                        )}

                        <Divider sx={styles.simpleDivider} />

                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={styles.fileListTools}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography level="title-sm">All Files</Typography>
                                <Chip
                                    variant="soft"
                                    size="sm"
                                    color={sortByRecent ? "primary" : "neutral"}
                                    onClick={() => setSortByRecent(!sortByRecent)}
                                    startDecorator={<Sort fontSize="small" />}
                                >
                                    {sortByRecent ? "Recent first" : "Alphabetical"}
                                </Chip>
                            </Stack>
                            <Button
                                variant="plain"
                                size="sm"
                                startDecorator={filesExpanded ? <ExpandLess /> : <ExpandMore />}
                                onClick={() => setFilesExpanded(!filesExpanded)}
                            >
                                {filesExpanded ? "Collapse" : "Expand"}
                            </Button>
                        </Stack>

                        {filesExpanded ? (
                            <List sx={styles.fileListContainer}>
                                {existingFiles.map((f) => (
                                    <ListItem key={f.id}>
                                        <ListItemButton
                                            component={ReactRouterLink}
                                            to={`/doc/${f.id}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSelectedFile(f);
                                                // Reset workflow if selecting a new file
                                                if (f.id !== selectedFile?.id) {
                                                    setCurrentStep(0);
                                                    setExtractedQuestion(null);
                                                    setNecessityCheck(null);
                                                    setRfiDraft(null);
                                                    setReviewStarted(false); // Enable Start New Review button after file select
                                                }
                                            }}
                                            sx={
                                                f.id === selectedFile?.id
                                                    ? styles.selectedItem
                                                    : undefined
                                            }
                                        >
                                            <Box sx={styles.fileItem}>
                                                <Box sx={styles.fileName}>
                                                    <FileIcon fileName={f.fileName} />
                                                    <Typography fontSize="sm">
                                                        {f.fileName}
                                                    </Typography>
                                                </Box>
                                                {f.id === selectedFile?.id && (
                                                    <CheckCircle sx={styles.checkIcon} />
                                                )}
                                            </Box>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography level="body-sm" sx={styles.hiddenFilesText}>
                                {existingFiles.length} files (hidden)
                            </Typography>
                        )}
                    </Stack>
                </Box>

                <Divider orientation="vertical" sx={styles.verticalDivider} />

                <Box sx={styles.mainContentContainer}>
                    <Stack sx={styles.workflowContainer} style={{ height: "100%" }}>
                        {selectedFile || uploadedFile || (currentStep > 0 && !selectedFile) ? (
                            <>
                                {currentFileViewerFile ? (
                                    <PanelGroup
                                        direction="horizontal"
                                        autoSaveId="rfi-review"
                                        style={{ margin: "0 -2px", height: "100%" }}
                                    >
                                        <Panel minSize={25} defaultSize={35} id="content" order={1}>
                                            <Box
                                                sx={{
                                                    height: "100%",
                                                    overflowY: "auto",
                                                    p: 2,
                                                }}
                                            >
                                                {/* Show file info cards inside the left panel */}
                                                {!selectedFile &&
                                                    uploadedFile &&
                                                    uploadedFile.progress > 0 &&
                                                    uploadedFile.progress !== -1 && (
                                                        <Card
                                                            variant="outlined"
                                                            sx={{
                                                                ...styles.spacingProcessingCard,
                                                                mb: 3,
                                                                borderRadius: "8px",
                                                                backgroundColor:
                                                                    "var(--joy-palette-background-surface)",
                                                            }}
                                                        >
                                                            <Typography level="title-sm">
                                                                Processing File (this may take a
                                                                while):
                                                            </Typography>
                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                                alignItems="center"
                                                                sx={styles.spacingMt1}
                                                            >
                                                                <FileIcon
                                                                    fileName={uploadedFile.name}
                                                                />
                                                                <Typography>
                                                                    {uploadedFile.name}
                                                                </Typography>
                                                                <CircularProgress
                                                                    size="sm"
                                                                    sx={styles.spacingMl1}
                                                                />
                                                            </Stack>
                                                        </Card>
                                                    )}

                                                {selectedFile && (
                                                    <Card
                                                        variant="outlined"
                                                        sx={{
                                                            ...styles.spacingProcessingCard,
                                                            mb: 3,
                                                            borderRadius: "8px",
                                                            backgroundColor:
                                                                "var(--joy-palette-background-surface)",
                                                        }}
                                                    >
                                                        <Typography level="title-sm">
                                                            Selected File:
                                                        </Typography>
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            alignItems="center"
                                                            sx={styles.spacingMt1}
                                                        >
                                                            <FileIcon
                                                                fileName={selectedFile.fileName}
                                                            />
                                                            <Typography>
                                                                {selectedFile.fileName}
                                                            </Typography>
                                                            {(isLoading || isRefreshingFiles) && (
                                                                <CircularProgress
                                                                    size="sm"
                                                                    sx={styles.spacingMl1}
                                                                />
                                                            )}
                                                        </Stack>
                                                    </Card>
                                                )}

                                                {!selectedFile && currentStep > 0 && (
                                                    <Card
                                                        variant="outlined"
                                                        sx={{
                                                            ...styles.spacingProcessingCard,
                                                            mb: 3,
                                                            borderRadius: "8px",
                                                            backgroundColor:
                                                                "var(--joy-palette-background-surface)",
                                                        }}
                                                    >
                                                        <Typography level="title-sm">
                                                            Text-based Processing
                                                        </Typography>
                                                        <Typography
                                                            level="body-sm"
                                                            sx={styles.spacingMt1}
                                                        >
                                                            Processing RFI directly from the
                                                            provided text input.
                                                        </Typography>
                                                    </Card>
                                                )}

                                                {renderStepContent()}
                                            </Box>
                                        </Panel>
                                        <StyledPanelResizeHandle style={{ margin: "0 2px" }} />
                                        <Panel minSize={40} defaultSize={65} id="file" order={2}>
                                            <Stack
                                                spacing={0}
                                                sx={{
                                                    height: "100%",
                                                    display: "flex",
                                                    flex: "1 1 auto",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <FileViewerMemo
                                                    fileArgs={currentFileViewerFile}
                                                    showTitleAndClose={true}
                                                    showSidebar={false}
                                                    onClose={() => {
                                                        setCurrentFileViewerFile(null);
                                                        setSelectedFragmentNode(null);
                                                    }}
                                                    onFragmentNodeSelected={(node) => {
                                                        setSelectedFragmentNode(node);
                                                    }}
                                                />
                                            </Stack>
                                        </Panel>
                                    </PanelGroup>
                                ) : (
                                    <Box
                                        sx={{
                                            height: "100%",
                                            overflowY: "auto",
                                            p: 2,
                                        }}
                                    >
                                        {/* Show file info cards */}
                                        {!selectedFile &&
                                            uploadedFile &&
                                            uploadedFile.progress > 0 &&
                                            uploadedFile.progress !== -1 && (
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        ...styles.spacingProcessingCard,
                                                        mb: 3,
                                                        borderRadius: "8px",
                                                        backgroundColor:
                                                            "var(--joy-palette-background-surface)",
                                                    }}
                                                >
                                                    <Typography level="title-sm">
                                                        Processing File (this may take a while):
                                                    </Typography>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                        sx={styles.spacingMt1}
                                                    >
                                                        <FileIcon fileName={uploadedFile.name} />
                                                        <Typography>{uploadedFile.name}</Typography>
                                                        <CircularProgress
                                                            size="sm"
                                                            sx={styles.spacingMl1}
                                                        />
                                                    </Stack>
                                                </Card>
                                            )}

                                        {selectedFile && (
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    ...styles.spacingProcessingCard,
                                                    mb: 3,
                                                    borderRadius: "8px",
                                                    backgroundColor:
                                                        "var(--joy-palette-background-surface)",
                                                }}
                                            >
                                                <Typography level="title-sm">
                                                    Selected File:
                                                </Typography>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={styles.spacingMt1}
                                                >
                                                    <FileIcon fileName={selectedFile.fileName} />
                                                    <Typography>{selectedFile.fileName}</Typography>
                                                    {(isLoading || isRefreshingFiles) && (
                                                        <CircularProgress
                                                            size="sm"
                                                            sx={styles.spacingMl1}
                                                        />
                                                    )}
                                                </Stack>
                                            </Card>
                                        )}

                                        {!selectedFile && currentStep > 0 && (
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    ...styles.spacingProcessingCard,
                                                    mb: 3,
                                                    borderRadius: "8px",
                                                    backgroundColor:
                                                        "var(--joy-palette-background-surface)",
                                                }}
                                            >
                                                <Typography level="title-sm">
                                                    Text-based Processing
                                                </Typography>
                                                <Typography level="body-sm" sx={styles.spacingMt1}>
                                                    Processing RFI directly from the provided text
                                                    input.
                                                </Typography>
                                            </Card>
                                        )}

                                        {renderStepContent()}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Card variant="outlined" sx={styles.welcomeCard}>
                                <Typography sx={styles.spacingMb2}>
                                    You can start the RFI review process in the following ways:
                                </Typography>
                                <Box sx={styles.welcomeTextContainer}>
                                    <ol>
                                        <li>
                                            <Typography sx={styles.spacingMb1}>
                                                <strong>Process text</strong> by typing or pasting
                                                RFI content directly in the text area (fastest
                                                option).
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography sx={styles.spacingMb1}>
                                                <strong>Upload a file</strong> (PDF, PNG, JPG,
                                                etc.).
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography>
                                                <strong>Select an existing file</strong> from the
                                                list on the left.
                                            </Typography>
                                        </li>
                                    </ol>
                                </Box>
                            </Card>
                        )}
                    </Stack>
                </Box>
            </Box>
        </Stack>
    );
}
